'use client';

import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAllocateDevice } from '@/hooks/usePartnerDevices';
import type { AssetClass, PartnerDetail, PoolDevice } from '@/lib/types';

interface AllocateDialogProps {
  partner: PartnerDetail;
  /** Null closes the dialog; a device opens it for that device. */
  device: PoolDevice | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * Hand a pooled device to a customer.
 *
 * This is the one step that creates the vehicle the customer works with, and it
 * stamps `allocatedAt` on it — telemetry is keyed by ident alone, so without
 * that floor a second customer would read where the box went for the first.
 */
export function AllocateDialog({ partner, device, onOpenChange }: AllocateDialogProps) {
  const [organizationId, setOrganizationId] = useState('');
  const [name, setName] = useState('');
  const [plateNumber, setPlateNumber] = useState('');
  const [assetClass, setAssetClass] = useState<AssetClass>('POWERED');

  const allocate = useAllocateDevice(partner.id);

  // The device's own name is the obvious first guess for the vehicle's.
  useEffect(() => {
    if (device) {
      setName(device.name);
      setPlateNumber('');
      setOrganizationId(partner.organizations[0]?.id ?? '');
    }
  }, [device, partner.organizations]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!device) return;

    if (!organizationId) {
      toast.error('Pick the customer this device is for');
      return;
    }
    if (!name.trim() || !plateNumber.trim()) {
      toast.error('A name and a plate number are required');
      return;
    }

    try {
      const result = await allocate.mutateAsync({
        deviceId: device.id,
        organizationId,
        name: name.trim(),
        plateNumber: plateNumber.trim(),
        assetClass,
      });
      toast.success(`${result.vehicle.name} created`, {
        description: `Ident ${device.ident} now reports into ${
          partner.organizations.find((org) => org.id === organizationId)?.name ?? 'the customer'
        }.`,
      });
      onOpenChange(false);
    } catch (error) {
      toast.error('Could not allocate the device', {
        description: error instanceof Error ? error.message : 'Something went wrong',
      });
    }
  };

  return (
    <Dialog open={device !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Allocate {device?.name}</DialogTitle>
            <DialogDescription>
              Creates the vehicle the customer sees. Its history starts now, not when the box was
              first switched on.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="allocate-org">Customer</Label>
              {partner.organizations.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Neither this partner nor any sub-dealer under it has a customer yet. Create an
                  organisation and set its partner to {partner.name} or to one below it.
                </p>
              ) : (
                <Select value={organizationId} onValueChange={setOrganizationId}>
                  <SelectTrigger id="allocate-org">
                    <SelectValue placeholder="Pick an organisation" />
                  </SelectTrigger>
                  <SelectContent>
                    {partner.organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.isOwn ? org.name : `${org.name} — ${org.partner?.name}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="allocate-name">Vehicle name</Label>
              <Input
                id="allocate-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Truck 14"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="allocate-plate">Plate number</Label>
              <Input
                id="allocate-plate"
                value={plateNumber}
                onChange={(event) => setPlateNumber(event.target.value)}
                placeholder="ABC 123"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="allocate-class">Asset class</Label>
              <Select
                value={assetClass}
                onValueChange={(value) => setAssetClass(value as AssetClass)}
              >
                <SelectTrigger id="allocate-class">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="POWERED">Powered — has an engine of its own</SelectItem>
                  <SelectItem value="UNPOWERED">Unpowered — trailer, container</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {device?.ident && (
              <p className="text-sm text-muted-foreground">
                Ident <code className="text-xs">{device.ident}</code>, Flespi device #
                {device.providerDeviceId}.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={allocate.isPending || partner.organizations.length === 0}
            >
              {allocate.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Allocate
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
